/**
 * @file EditExtractedInformationSkeleton.tsx
 *
 * @description This component renders a loading skeleton for the Edit Extracted Information page
 * of the edit extracted information page management system. It provides visual feedback to officers
 * while the actual form data is being loaded from the Django backend.
 *
 * @module Components/Skeletons
 * @requires Material-UI
 * @requires React
 */

import { Box, Paper, Skeleton } from "@mui/material";
import Grid from "@mui/material/Grid";

/**
 * @function EditExtractedInformationSkeleton
 *
 * @description A placeholder loading interface that mirrors the structure of the actual
 * evidence extraction form. This component is displayed while evidence data is being
 * fetched from the server or processed. It provides a visual indication of the layout
 * to reduce perceived loading time and improve user experience for police officers.
 *
 * The skeleton includes placeholders for:
 * - Video evidence preview section
 * - Navigation sidebar with evidence extraction steps
 * - Form fields for extracted information
 * - Form navigation controls
 * - Verification and submission sections
 *
 * @returns {JSX.Element} A skeleton loading interface that mimics the layout of the editable
 *                       evidence extracted information form.
 */
export const EditExtractedInformationSkeleton = () => {
  return (
    <Box>
      <Grid container spacing={2}>
        {/* Main Form Area */}
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 3, position: "relative" }}>
            {/* Undo Section Button Skeleton */}
            {/* <Box sx={{ mb: 3, display: "flex", justifyContent: "flex-end" }}>
              <Skeleton variant="rectangular" width={180} height={36} />
            </Box> */}

            {/* Progress indicator skeleton - shows user where they are in the multi-step form process */}
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  position: "relative",
                  width: "100%",
                  top: -15,
                  mb: 2,
                  mt: 4,
                }}
              >
                {[1, 2, 3, 4, 5].map((i) => (
                  <Skeleton key={i} variant="circular" width={20} height={20} />
                ))}
              </Box>
            </Box>

            {/* Main form content area */}
            <Box sx={{ maxHeight: "65vh", pr: 2, mb: 5 }}>
              {/* Form section heading */}
              <Skeleton variant="text" width="40%" height={40} sx={{ mb: 3 }} />

              {/* Grid of form fields - represents input fields for evidence metadata */}
              <Grid container spacing={2}>
                {/* Each grid item represents a form field (name, date, location, etc.) */}
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                  <Grid key={i} size={4}>
                    <Box sx={{ m: 0.5 }}>
                      <Skeleton
                        variant="rectangular"
                        height={56}
                        width="100%"
                      />
                    </Box>
                  </Grid>
                ))}
              </Grid>

              {/* Special form field - typically a dropdown or more complex input */}
              <Box sx={{ mt: 4, mb: 2 }}>
                <Skeleton variant="rectangular" height={56} width="100%" />
              </Box>

              {/* Large text areas - notes/descriptions */}
              <Box sx={{ mb: 4 }}>
                <Skeleton
                  variant="rectangular"
                  height={100}
                  width="100%"
                  sx={{ mb: 2 }}
                />
                <Skeleton variant="rectangular" height={100} width="100%" />
              </Box>

              {/* Verification checkbox - for legal compliance and evidence verification */}
              <Box sx={{ mt: 4, textAlign: "center" }}>
                <Skeleton
                  variant="rectangular"
                  width={400}
                  height={36}
                  sx={{ mx: "auto" }}
                />
              </Box>
            </Box>

            {/* Form navigation controls - prev/next step buttons */}
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                mt: 3,
                borderTop: "1px solid rgba(0,0,0,0.12)",
                pt: 2,
              }}
            >
              {/* Previous button */}
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mx: 1 }}
              />
              {/* Step indicator */}
              <Skeleton variant="text" width={50} height={40} sx={{ mx: 2 }} />
              {/* Next button */}
              <Skeleton
                variant="circular"
                width={40}
                height={40}
                sx={{ mx: 1 }}
              />
            </Box>

            {/* Form submission area */}
            <Box sx={{ mt: 4, borderTop: "1px solid rgba(0,0,0,0.12)", pt: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
                <Skeleton variant="rectangular" width={500} height={36} />
              </Box>
              {/* Submit button */}
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Skeleton
                  variant="rectangular"
                  width={200}
                  height={48}
                  sx={{ borderRadius: 1 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};
